import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { track } from "@/lib/track";
import { startViewTransition } from "@/lib/view-transition";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";
import heroVideo from "@/assets/hero.mp4";
import heroPoster from "@/assets/hero-poster.jpg";

export function Hero() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const fitHref = localizedHref("/fit");
  const [mounted, setMounted] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  React.useEffect(() => setMounted(true), []);

  // iOS Safari (iPad/iPhone) requires the video element to be programmatically
  // confirmed as muted + playsInline, and for .play() to be invoked, before it
  // will honor autoplay. Otherwise it shows a large fallback play button.
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    // If the tab was backgrounded during initial mount, retry when it returns.
    const onVisible = () => {
      if (document.visibilityState === "visible") tryPlay();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const goToFit = (e: React.MouseEvent<HTMLAnchorElement>) => {
    track("fit_click", { source: "hero" });
    // Let modifier-clicks (open in new tab, etc.) and middle clicks behave
    // normally; only intercept plain left-clicks to run the cross-fade.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    startViewTransition(async () => {
      await navigate({ to: fitHref });
    });
  };

  return (
    <section
      id="hero"
      className="frame relative min-h-[calc(100svh-20px)] w-full sm:h-[calc(100svh-20px)] sm:min-h-[760px]"
    >
      {/* Background video */}
      <div className="absolute inset-0">
        {/* Cream→sage gradient as instant background while video loads */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-[#FBF1D6] via-[#E8DDC2] to-[#A6B89E]"
        />
        {/* Poster image as a real <img> so it follows object-cover.
            The native `poster` attribute ignores object-fit in Safari/Firefox,
            so we render it underneath and let the video overlay it on play. */}
        <img
          aria-hidden="true"
          src={heroPoster}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <video
          ref={videoRef}
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          src={heroVideo}
        />
        {/* Subtle whitewash for text legibility */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-white/0 via-white/5 to-white/30"
        />
        {/* Bottom fade + blur to hide the Veo watermark */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-0 bottom-0 left-0 h-32 backdrop-blur-[5px]"
          style={{
            maskImage: "linear-gradient(to top, black 36%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to top, black 36%, transparent 100%)",
          }}
        />
      </div>

      {/* Foreground content */}
      <div className="relative z-10 flex h-full flex-col">
        {/* Spacer for navbar — shrink-0 so flex can't collapse it */}
        <div className="h-28 shrink-0" />

        {/* Centered body */}
        <div
          className={cn(
            "flex flex-1 flex-col items-center justify-start px-6 pt-6 text-center sm:justify-center sm:pt-0",
            "transition-all duration-700",
            mounted ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
          )}
        >
          {/* Headline — altitude metaphor as the dominant statement,
              with three punchy fragments at ~half size as a sub-deck.
              "altitude" and "AI" get the italic accent treatment. */}
          <h1 className="max-w-[28ch] text-[clamp(40px,8vw,84px)] leading-[1.02] font-medium tracking-[-0.035em] text-ink">
            <Trans
              i18nKey="landing.hero.headline"
              components={{
                br: <br />,
                italic: <span className="serif-italic text-accent" />,
              }}
            />
            <span className="mt-4 block text-[clamp(22px,3.5vw,40px)] leading-[1.15] font-medium tracking-[-0.02em]">
              <Trans
                i18nKey="landing.hero.subhead"
                components={{
                  italic: <span className="serif-italic text-accent" />,
                }}
              />
            </span>
          </h1>

          {/* Subtitle */}
          <p className="mt-5 max-w-[58ch] text-[clamp(15px,1.4vw,17px)] leading-[1.55] text-ink-2">
            {t("landing.hero.subtitle")}
          </p>

          {/* CTAs — /fit is the primary funnel (large orange pill with a
              soft accent halo + slow shimmer sweep); beneath it, a short
              conversational line whose accent link scrolls to the final
              CTA band. */}
          <div className="mt-8 flex flex-col items-center gap-5">
            {/* Halo wrapper — the absolutely-positioned blurred glow lives
                outside the button so `overflow: hidden` on the button can
                clip the shimmer without clipping the halo. */}
            <div className="relative inline-block">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -inset-4 rounded-full bg-accent/35 blur-2xl"
              />
              <Link
                to={fitHref}
                onClick={goToFit}
                className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-accent py-3 pr-3 pl-7 text-[15px] font-semibold tracking-tight text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-accent-hover hover:shadow-lg hover:shadow-accent/30"
              >
                {/* Shimmer sweep — slow (~5s), narrow band, low opacity.
                    Lives behind the label so it reads as a sheen on the
                    pill surface, not a separate moving element. */}
                <span
                  aria-hidden="true"
                  className="animate-cta-shimmer pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.28) 50%, transparent 65%)",
                  }}
                />
                <span className="relative">{t("landing.hero.cta")}</span>
                <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/20 transition-transform duration-200 group-hover:translate-x-0.5">
                  <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
                </span>
              </Link>
            </div>
            <p className="text-[14px] tracking-tight text-ink-2">
              {t("landing.hero.ctaSecondaryLead")}{" "}
              <a
                href="#cta"
                onClick={() => track("cta_scroll", { source: "hero" })}
                className="font-medium text-accent underline decoration-accent/40 underline-offset-4 transition-colors hover:decoration-accent"
              >
                {t("landing.hero.ctaSecondary")}
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
